<template>
  <div class="user-search-select" ref="container">
    <input
      type="text"
      class="uss-input"
      :placeholder="placeholder"
      :disabled="disabled"
      v-model="query"
      @focus="open = true"
      @keydown.escape="close"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.down.prevent="moveHighlight(1)"
      @keydown.up.prevent="moveHighlight(-1)"
    />
    <ul v-if="open && filteredUsers.length" class="uss-dropdown">
      <li
        v-for="(user, i) in filteredUsers"
        :key="user.PortalUserId || user.portalUserId || user.PrimaryKey || user.primaryKey"
        class="uss-option"
        :class="{ highlighted: i === highlightIndex }"
        @mousedown.prevent="selectUser(user)"
      >{{ displayName(user) }}</li>
    </ul>
    <div v-else-if="open && query && !filteredUsers.length" class="uss-empty">No matches</div>
  </div>
</template>

<script>
export default {
  name: 'UserSearchSelect',
  props: {
    users: { type: Array, default: () => [] },
    modelValue: { type: [String, Number], default: '' },
    placeholder: { type: String, default: 'Search...' },
    disabled: { type: Boolean, default: false }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      query: '',
      open: false,
      highlightIndex: 0
    };
  },
  computed: {
    sortedUsers() {
      return [...this.users].sort((a, b) =>
        this.displayName(a).localeCompare(this.displayName(b))
      );
    },
    filteredUsers() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.sortedUsers;
      return this.sortedUsers.filter(u =>
        this.displayName(u).toLowerCase().includes(q)
      );
    }
  },
  watch: {
    modelValue(val) {
      if (!val) { this.query = ''; return; }
      const match = this.users.find(u => (u.PrimaryKey || u.primaryKey) == val);
      if (match) this.query = this.displayName(match);
    },
    query() {
      this.highlightIndex = 0;
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.onOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onOutsideClick);
  },
  methods: {
    displayName(user) {
      return user.Username || user.username || user.EmailAddress || user.emailAddress || '';
    },
    selectUser(user) {
      this.query = this.displayName(user);
      this.$emit('update:modelValue', user.PortalUserId || user.portalUserId || user.PrimaryKey || user.primaryKey);
      this.open = false;
    },
    close() {
      this.open = false;
    },
    selectHighlighted() {
      if (this.filteredUsers[this.highlightIndex]) {
        this.selectUser(this.filteredUsers[this.highlightIndex]);
      }
    },
    moveHighlight(dir) {
      const max = this.filteredUsers.length - 1;
      this.highlightIndex = Math.max(0, Math.min(max, this.highlightIndex + dir));
    },
    onOutsideClick(e) {
      if (!this.$refs.container?.contains(e.target)) this.open = false;
    }
  }
};
</script>

<style scoped>
.user-search-select {
  position: relative;
}

.uss-input {
  width: 100%;
  padding: 9px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #111;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.uss-input:focus { border-color: #3986A8; }
.uss-input:disabled { background: #f9fafb; color: #6b7280; cursor: default; }

.uss-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 200;
  list-style: none;
  margin: 0;
  padding: 4px 0;
}

.uss-option {
  padding: 8px 12px;
  font-size: 14px;
  color: #111;
  cursor: pointer;
}
.uss-option:hover,
.uss-option.highlighted { background: #e8f4f8; color: #237091; }

.uss-empty {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #9ca3af;
  z-index: 200;
}
</style>
